/*
 *************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 *************************************************************************
 */

type Environment = 'dev' | 'stage' | 'prod';

// Pre-compute arrays once instead of on every call
const stageUrlFragments = [
	'stage-nest',
	'stage1-nest',
	'stage2-nest',
	'stage3-nest',
	'stage',
];
const devUrlFragments = [
	'dev-nest',
	// dev1-nest through dev30-nest
	...Array.from({length: 30}, (_, i) => `dev${i + 1}-nest`),
	'dev.hollywoodstudios',
	'stage2.', //the dot here is important to avoid matching stage2-nest
	'dev-pr-nest', // PR preview for dev/stage (prod PR previews use pr-nest without dev- prefix)
];

// Memoize result since hostname doesn't change during runtime
let cachedEnvironment: Environment | null = null;

const getEnvironment = (): Environment => {
	// Return cached value if available
	if (cachedEnvironment !== null) {
		return cachedEnvironment;
	}

	const {hostname} = window.location;

	// Check dev first (most common in development)
	const isDev = devUrlFragments.some((devUrlFragment) =>
		hostname.includes(devUrlFragment),
	);
	if (isDev) {
		cachedEnvironment = 'dev';
		return cachedEnvironment;
	}

	// Check stage
	const isStage = stageUrlFragments.some((stageUrlFragment) =>
		hostname.includes(stageUrlFragment),
	);
	if (isStage) {
		cachedEnvironment = 'stage';
		return cachedEnvironment;
	}

	// Default to prod
	cachedEnvironment = 'prod';
	return cachedEnvironment;
};

type NRErrorHandler = (error: Error) => boolean;

const newRelicErrorHandler: NRErrorHandler = (error) => {
	// If an error is destined for New Relic, it will show here first. return false to prevent it from being sent.

	// If on dev, log to the console
	if (getEnvironment() === 'dev') {
		// eslint-disable-next-line no-console
		console.error('[New Relic] Caught an error:', error);
	}

	const ignoreStrings: string[] = [
		'nr-spa-recorder', // new relic scripts are throwing errors, we believe it has to do with session replays
		'Unhandled Promise Rejection: {"exception":{}}', // this seems to be a high, re-occurring error that has no stack trace; it's possible this error triggers from a 3rd party script, a common extension, and/or something not traceable in Nest itself
		'Script error.', // part of 3rd party scripts according to New Relic
		"Cannot read properties of undefined (reading 'DomainData')", // This comes from the cdn.cookielaw.org script, which is part of OneTrust
		// ResizeObserver loop issue seems to come from React Spectrum, though no stack trace in NR. All instances of ResizeObserver in nest seem to be properly unobserved/disconnected
		// Documentation for "Manage People Mini-App" on this error recommends to ignore it:
		// https://git.corp.adobe.com/PandoraUI/administration-mini-apps/tree/feature-orchestration/packages/react-mini-app-be-manage-people#issue-details
		'ResizeObserver loop completed with undelivered notifications.',
		'',
	];

	for (const ignoreString of ignoreStrings) {
		if (error.stack && error.stack.toString().includes(ignoreString)) {
			return true;
		} else if (
			error.message &&
			error.message.toString().includes(ignoreString)
		) {
			return true;
		}
	}

	return false;
};

const Utils = {
	getEnvironment,
	newRelicErrorHandler,
};

export {Utils};
